// Mirror of the backend response shapes (packages/backend/src/types.ts).

export type AppType = "fargate" | "serverless" | "static";
export type Status = "crit" | "warn" | "unknown" | "ok";
export type AlarmState = "OK" | "ALARM" | "INSUFFICIENT_DATA";

export type TimeRange = "1h" | "3h" | "12h" | "1d" | "3d" | "1w";
export const TIME_RANGES: TimeRange[] = ["1h", "3h", "12h", "1d", "3d", "1w"];

export interface Vital {
  label: string;
  value: string;
  series: number[];
  timestamps?: number[];
}

export interface AppCard {
  id: string;
  displayName: string;
  env: "dev" | "prod";
  type: AppType;
  status: Status;
  vitals: Vital[];
  alarmCount: number;
  updatedAt: string;
  reachable: boolean;
  owner?: string;
  uptime24h?: number | null;
  costPerDay?: number | null;
  lastChangeAt?: string | null;
}

export interface Dependency { name: string; sub: string; status: Status; }

export interface OverviewResponse {
  generatedAt: string;
  apps: AppCard[];
  summary: { ok: number; warn: number; crit: number; unknown: number; total: number };
  unreachable: string[];
  fleetRibbon?: Array<{ t: number; worst: "crit" | "ok" }>;
  spendToday?: number | null;
}

export interface AlarmInfo {
  name: string;
  state: AlarmState;
  severity: "critical" | "warning";
  since?: string;
  reason?: string;
  acked?: boolean;
  snoozedUntil?: string;
  spans?: Array<{ l: number; w: number }>;
}

export type Intent = "good" | "warn" | "bad" | "neutral";

export interface MetricTile {
  label: string;
  value: string;
  unit?: string;
  series: number[];
  timestamps?: number[];
  intent?: Intent;
}

export interface MetricSection {
  key: string;
  title: string;
  subtitle?: string;
  tiles: MetricTile[];
}

export interface EndpointStat {
  method: string;
  path: string;
  count: number;
  p95: number;
  errorRate: number;
}

export interface AppDetail extends AppCard {
  alarms: AlarmInfo[];
  chart: { title: string; series: number[]; timestamps?: number[] };
  range: TimeRange;
  sections?: MetricSection[];
  dependencies?: Dependency[];
  endpoints?: EndpointStat[];
}

export const STATUS_LABEL: Record<Status, string> = {
  ok: "Operational",
  warn: "Degraded",
  crit: "Critical",
  unknown: "Unreachable",
};
